import MapIcon from '@mui/icons-material/Map';
import { Button, Skeleton, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import HumidityMapModal from '/@/components/humidity-map-modal';
import { useHumidityMaps } from '/@/hooks/maps';
import { getClimatePatternChoices, getOfficialClimatePattern } from '/@/hooks/recommendation';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { createPlantingDateValidator } from '/@/utils/date';
import ChipSelect from './chip-select';

/** Shape of the humidity overlay the map modal expects; `useHumidityMaps` is untyped. */
type HumidityMapVersion = { east: number; north: number; south: number; west: number };
type HumidityMap = { type: string; versions: HumidityMapVersion[] };

/** Height of the date input, and of a row of chips — so the skeleton doesn't shift on swap. */
const DATE_INPUT_HEIGHT = 56;
const CHIP_ROW_HEIGHT = 32;

/**
 * Stand-in for the whole row while the choices are still resolving.
 *
 * Soil moisture is the slow one — its options depend on the planting date and climate year —
 * and rendering the other two immediately made it pop in beside them a moment later. Covering
 * all three keeps the row one object that resolves once.
 */
function ConditionsSkeleton({ isMobile }: { isMobile: boolean }) {
    return (
        <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems='flex-start'>
            {[DATE_INPUT_HEIGHT, CHIP_ROW_HEIGHT, CHIP_ROW_HEIGHT].map((height, index) => (
                <Stack
                    // biome-ignore lint/suspicious/noArrayIndexKey: three fixed placeholder columns
                    key={index}
                    spacing={2}
                    flexGrow={1}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                >
                    <Skeleton variant='text' width={140} height={24} />
                    <Skeleton variant='rounded' width={isMobile ? '100%' : 220} height={height} />
                </Stack>
            ))}
        </Stack>
    );
}

/**
 * Planting date, soil moisture and climate year — the three inputs every corn recommendation
 * needs before anything can be estimated.
 *
 * Presentational on purpose: the hybrid wizard asks for these with a field already chosen and
 * sources them from that field's zones, while the product-first wizard asks for them first and
 * sources them from the product's. Both render this, so the fields themselves can't drift.
 */
function ConditionsFields({
    plantingDate,
    soilMoisture,
    climatePattern,
    soilMoistureChoices,
    climatePatternChoices,
    validPlantingDates,
    isLoading,
    isInitialLoading,
    onChange,
    crop = 'corn',
    /** Only needed for the humidity map shortcut, which is Argentina-only. */
    field,
    countryCode,
}: {
    plantingDate?: string;
    soilMoisture?: number;
    climatePattern?: number;
    soilMoistureChoices: { value: number; label: string }[];
    climatePatternChoices?: { value: number; label: string }[];
    validPlantingDates?: [string, string][];
    /** Disables the controls while a dependent query is in flight. */
    isLoading?: boolean;
    /** Nothing is known yet — the whole row is a skeleton rather than a partial one. */
    isInitialLoading?: boolean;
    onChange: (key: string) => (value: unknown) => void;
    crop?: 'corn' | 'soy';
    field?: Field;
    countryCode?: string;
}) {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const sessionCountry = useSelector(getSessionCountry);
    const country = countryCode || field?.country?.code || sessionCountry?.code;

    const { toggle: toggleHumidityMapModal, open: humidityMapModalOpened } = useToggle(false);
    const [mapVersion, setMapVersion] = useState<HumidityMapVersion | undefined>(undefined);
    const { data: maps } = useHumidityMaps({ country });
    const humidityMaps = ((maps || []) as HumidityMap[]).find((map) => map.type === 'humidity');

    useEffect(() => {
        if (humidityMaps?.versions) setMapVersion(humidityMaps.versions[0]);
    }, [humidityMaps]);

    // Countries can publish an official forecast; when they do it is what the chips start on,
    // and this line says so. Countries without one just get the Neutral default, unexplained.
    const officialClimatePatternLabel = useMemo(() => {
        const official = getOfficialClimatePattern(country);
        if (official === undefined) return undefined;
        const label = (climatePatternChoices || getClimatePatternChoices())?.find(
            (choice: { value: number }) => choice.value === official
        )?.label;
        return label && _('Pronóstico oficial: {{pattern}}', { pattern: label });
    }, [country, climatePatternChoices]);

    // Planting windows come from the zones; everything outside them is greyed out in the
    // calendar rather than being selectable and then rejected.
    const shouldDisableDate = useMemo(() => createPlantingDateValidator(validPlantingDates), [validPlantingDates]);

    if (isInitialLoading) return <ConditionsSkeleton isMobile={isMobile} />;

    return (
        <>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems='flex-start'>
                <Stack spacing={2} flexGrow={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('Fecha de siembra')}
                    </Typography>
                    <MuiDatePicker
                        value={plantingDate ? dayjs(plantingDate) : null}
                        onChange={(date) => onChange('plantingDate')(date ? date.format('YYYY-MM-DD') : '')}
                        shouldDisableDate={shouldDisableDate}
                        disabled={isLoading}
                    />
                </Stack>

                {soilMoistureChoices.length > 0 && (
                    <ChipSelect
                        title={_('Humedad de suelo')}
                        options={soilMoistureChoices}
                        value={soilMoisture}
                        onChange={onChange('soilMoisture')}
                        disabled={isLoading}
                        stretch={isMobile}
                        dataTestId='environment-step-soil-moisture-chips'
                        action={
                            // The humidity map needs a full-size map canvas, so the design drops
                            // the shortcut on phones.
                            !isMobile &&
                            country === 'AR' && (
                                <Button
                                    variant='text'
                                    color='primary'
                                    startIcon={<MapIcon />}
                                    onClick={toggleHumidityMapModal}
                                    sx={{ textDecoration: 'underline', p: 0 }}
                                >
                                    {_('Ver mapa')}
                                </Button>
                            )
                        }
                    />
                )}

                {crop === 'corn' && (
                    <ChipSelect
                        title={_('Año climático')}
                        options={climatePatternChoices || getClimatePatternChoices()}
                        value={climatePattern}
                        onChange={onChange('climatePattern')}
                        disabled={isLoading}
                        stretch={isMobile}
                        dataTestId='environment-step-climate-pattern-chips'
                        helperText={officialClimatePatternLabel}
                    />
                )}
            </Stack>

            {humidityMapModalOpened && (
                <HumidityMapModal
                    open={humidityMapModalOpened}
                    toggleHumidityMapModal={toggleHumidityMapModal}
                    field={field}
                    mapVersion={{ version: mapVersion, overlay: humidityMaps }}
                    handleOverlayChange={(overlay) => {
                        if (typeof overlay?.version === 'number')
                            setMapVersion(humidityMaps?.versions[overlay.version]);
                    }}
                />
            )}
        </>
    );
}

export default ConditionsFields;
